import { gql, useMutation } from '@apollo/client'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuth } from '@features/auth'
import { useErrorInfoModal } from '@features/modal'

import Button from '@shared/ui/button'
import Checkbox from '@shared/ui/checkbox'
import MaskedInput from '@shared/ui/input-mask'
import { PHONE_MASK_LENGTH } from '@shared/validation'

import styles from './DeliveryView.module.scss'

const REQUEST_ADDRESS_FOR_DELIVERY = gql`
  mutation requestAddressForDelivery($request: AddressForDeliveryRequestInput!) {
    requestAddressForDelivery(request: $request)
  }
`

interface RequestDeliveryFormProps {
  address: string;
  geoPoint: { latitude: number; longitude: number } | null;
  onSuccess?: () => void;
}

export default function RequestDeliveryForm ({ 
  address, 
  geoPoint,
  onSuccess 
}: RequestDeliveryFormProps) {
  const { t } = useTranslation('modalDelivery')
  const { t: tc } = useTranslation('common')
  const { t: te } = useTranslation('errors')
  const { user } = useAuth()
  const { openErrorInfoModal } = useErrorInfoModal()

  const [ phone, setPhone ] = useState(user?.phone || '')
  const [ agreed, setAgreed ] = useState(false)
  const [ loading, setLoading ] = useState(false)
  const [ success, setSuccess ] = useState(false)

  const [ sendRequest ] = useMutation(REQUEST_ADDRESS_FOR_DELIVERY)

  const isPhoneValid = phone.length >= PHONE_MASK_LENGTH

  const handleSubmit = async () => {
    if (!agreed || !isPhoneValid) return

    setLoading(true)

    try {
      const meta = geoPoint ? JSON.stringify({ point: geoPoint }) : undefined
      
      const result = await sendRequest({
        variables: {
          request: {
            address,
            phone: phone.replace(/\D/g, ''),
            first_name: user?.first_name || '',
            last_name: user?.last_name || '',
            meta,
          }
        }
      })

      if (result.data?.requestAddressForDelivery) {
        setSuccess(true)
        setTimeout(() => {
          onSuccess?.()
        }, 2000)
      }
    }
    catch (error) {
      console.error('Failed to send delivery request:', error)
      openErrorInfoModal({
        description: te('send_request'),
      })
    }
    finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className={styles.requestForm}>
        <p className={styles.requestForm__success}>
          {t('request.success')}
        </p>
      </div>
    )
  }

  return (
    <div className={styles.requestForm}>
      <p className={styles.requestForm__title}>
        {t('request.title')}
      </p>
      <p className={styles.requestForm__description}>
        {t('request.description')}
      </p>
      
      <div className={styles.requestForm__field}>
        <MaskedInput
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+7 ___ ___ __ __"
        />
      </div>

      <div className={styles.requestForm__checkbox}>
        <Checkbox
          checked={agreed}
          onChange={() => setAgreed(!agreed)}
          label={t('request.agree')}
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={!agreed || !isPhoneValid || loading}
        theme="primary"
        size="large"
      >
        {loading ? tc('loading') : t('request.submit')}
      </Button>
    </div>
  )
}
